import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface SocialNetwork {
  id: string;
  type: string;
  name: string;
  url: string;
  enabled: boolean;
  position: number;
  chatbot_public_id: string;
}

interface SocialMediaState {
  socialNetworksByChatbot: Record<string, SocialNetwork[]>;
  loading: boolean;
  error: string | null;
  initialized: Record<string, boolean>;
  initialize: (chatbotPublicId: string) => Promise<void>;
  addNetwork: (chatbotPublicId: string, network: Omit<SocialNetwork, 'id' | 'position' | 'chatbot_public_id'>) => Promise<void>;
  updateNetwork: (chatbotPublicId: string, networkId: string, updates: Partial<SocialNetwork>) => Promise<void>;
  deleteNetwork: (chatbotPublicId: string, networkId: string) => Promise<void>;
  reorderNetworks: (chatbotPublicId: string, networkIds: string[]) => Promise<void>;
  toggleNetwork: (chatbotPublicId: string, networkId: string) => Promise<void>;
  getSocialNetworks: (chatbotPublicId: string) => SocialNetwork[];
}

export const useSocialMediaSystem = create<SocialMediaState>((set, get) => ({
  socialNetworksByChatbot: {},
  loading: false,
  error: null,
  initialized: {},

  getSocialNetworks: (chatbotPublicId: string) => {
    const networks = get().socialNetworksByChatbot[chatbotPublicId] || [];
    return networks;
  },

  initialize: async (chatbotPublicId) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      set({ loading: true, error: null });

      // First, check if the social_networks table exists
      try {
        await supabase.rpc('create_social_networks_table');
      } catch (err) {
        // Table might already exist, continue
      }

      // Fetch social networks directly using chatbot_public_id
      const { data, error } = await supabase
        .from('social_networks')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (error) {
        throw error;
      }
      
      set(state => ({
        socialNetworksByChatbot: {
          ...state.socialNetworksByChatbot,
          [chatbotPublicId]: data || []
        },
        initialized: {
          ...state.initialized,
          [chatbotPublicId]: true
        },
        loading: false
      }));
    } catch (err) {
      set(state => ({
        error: err instanceof Error ? err.message : 'Error initializing social media',
        loading: false,
        initialized: {
          ...state.initialized,
          [chatbotPublicId]: true
        },
        socialNetworksByChatbot: {
          ...state.socialNetworksByChatbot,
          [chatbotPublicId]: []
        }
      }));
    }
  },

  addNetwork: async (chatbotPublicId, network) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      set({ loading: true, error: null });

      // Get current max position
      const currentNetworks = get().socialNetworksByChatbot[chatbotPublicId] || [];
      const maxPosition = Math.max(0, ...currentNetworks.map(n => n.position));

      // Insert new network
      const { data, error } = await supabase
        .from('social_networks')
        .insert({
          chatbot_public_id: chatbotPublicId,
          type: network.type,
          name: network.name,
          url: network.url,
          enabled: network.enabled,
          position: maxPosition + 1
        })
        .select()
        .single();

      if (error) throw error;
      
      if (data) {
        const newService = data as SocialNetwork;
        set(state => ({
          socialNetworksByChatbot: {
            ...state.socialNetworksByChatbot,
            [chatbotPublicId]: [...(state.socialNetworksByChatbot[chatbotPublicId] || []), newService]
          },
          loading: false
        }));
      }
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Error adding social network',
        loading: false
      });
    }
  },

  updateNetwork: async (chatbotPublicId, networkId, updates) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      set({ loading: true, error: null });

      // First verify the network belongs to the correct chatbot
      const { data: existingNetwork, error: fetchError } = await supabase
        .from('social_networks')
        .select('*')
        .eq('id', networkId)
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (fetchError) throw fetchError;
      if (!existingNetwork) {
        throw new Error('Network not found or does not belong to this chatbot');
      }

      // Update network
      const { error } = await supabase
        .from('social_networks')
        .update(updates)
        .eq('id', networkId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        socialNetworksByChatbot: {
          ...state.socialNetworksByChatbot,
          [chatbotPublicId]: (state.socialNetworksByChatbot[chatbotPublicId] || []).map(network =>
            network.id === networkId ? { ...network, ...updates } : network
          )
        },
        loading: false
      }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Error updating social network',
        loading: false
      });
    }
  },

  deleteNetwork: async (chatbotPublicId, networkId) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      set({ loading: true, error: null });

      // First verify the network belongs to the correct chatbot
      const { data: existingNetwork, error: fetchError } = await supabase
        .from('social_networks')
        .select('*')
        .eq('id', networkId)
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (fetchError) throw fetchError;
      if (!existingNetwork) {
        throw new Error('Network not found or does not belong to this chatbot');
      }

      // Delete network
      const { error } = await supabase
        .from('social_networks')
        .delete()
        .eq('id', networkId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        socialNetworksByChatbot: {
          ...state.socialNetworksByChatbot,
          [chatbotPublicId]: (state.socialNetworksByChatbot[chatbotPublicId] || []).filter(network => network.id !== networkId)
        },
        loading: false
      }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Error deleting social network',
        loading: false
      });
    }
  },

  reorderNetworks: async (chatbotPublicId, networkIds) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      set({ loading: true, error: null });

      // Get current networks to validate IDs
      const currentNetworks = get().socialNetworksByChatbot[chatbotPublicId] || [];
      const validNetworkIds = currentNetworks.map(n => n.id);
      
      // Validate all IDs belong to this chatbot
      const validIds = networkIds.filter(id => validNetworkIds.includes(id));
      if (validIds.length !== networkIds.length || validIds.length !== currentNetworks.length) {
        throw new Error('Invalid network IDs');
      }

      // Update positions one by one
      const updates = validIds.map((id, index) => {
        return supabase
          .from('social_networks')
          .update({ position: index })
          .eq('id', id)
          .eq('chatbot_public_id', chatbotPublicId);
      });

      // Execute all updates
      await Promise.all(updates);

      // Update local state
      const updatedNetworks = [...currentNetworks].sort((a, b) => {
        return networkIds.indexOf(a.id) - networkIds.indexOf(b.id);
      }).map((network, index) => ({
        ...network,
        position: index
      }));

      set(state => ({
        socialNetworksByChatbot: {
          ...state.socialNetworksByChatbot,
          [chatbotPublicId]: updatedNetworks
        },
        loading: false
      }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Error reordering social networks',
        loading: false
      });
    }
  },

  toggleNetwork: async (chatbotPublicId, networkId) => {
    try {
      if (!chatbotPublicId) {
        throw new Error('chatbotPublicId is required');
      }
      const networks = get().socialNetworksByChatbot[chatbotPublicId] || [];
      const network = networks.find(n => n.id === networkId);
      if (!network) {
        throw new Error('Network not found');
      }
      if (network.chatbot_public_id !== chatbotPublicId) {
        throw new Error('Network does not belong to this chatbot');
      }

      await get().updateNetwork(chatbotPublicId, networkId, { enabled: !network.enabled });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Error toggling social network'
      });
    }
  }
}));